/**
 * 跨栈 API 响应适配：Python FastAPI / Java AjaxResult / TableDataInfo / 裸 JSON
 */

export interface AjaxResult<T = unknown> {
  code: number
  msg: string
  data?: T
}

export interface TableDataInfo<T = unknown> {
  code: number
  msg: string
  total: number
  rows: T[]
}

export type ApiFormatHint = 'auto' | 'ajax' | 'table' | 'python' | 'raw'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** 判断业务是否成功 */
export function isSuccess(body: unknown): boolean {
  if (!isRecord(body)) return true
  if ('success' in body && body.success === false) return false
  if ('code' in body && typeof body.code === 'number' && body.code !== 200) return false
  return true
}

/** 从多种信封中提取 data */
export function unwrapData<T>(body: unknown): T {
  if (!isRecord(body)) return body as T
  if ('data' in body && body.data !== undefined) return body.data as T
  if ('success' in body && body.success === false) {
    throw new Error(String(body.message ?? body.msg ?? '请求失败'))
  }
  return body as T
}

/** 归一化列表为 { rows, total } */
export function unwrapList<T>(body: unknown): { rows: T[]; total: number } {
  if (!isRecord(body)) {
    return { rows: Array.isArray(body) ? (body as T[]) : [], total: 0 }
  }

  if (Array.isArray(body.rows)) {
    return {
      rows: body.rows as T[],
      total: typeof body.total === 'number' ? body.total : body.rows.length,
    }
  }

  if (Array.isArray(body.textbooks)) {
    return {
      rows: body.textbooks as T[],
      total: typeof body.total === 'number' ? body.total : body.textbooks.length,
    }
  }

  if (Array.isArray(body.users)) {
    return {
      rows: body.users as T[],
      total: typeof body.total === 'number' ? body.total : body.users.length,
    }
  }

  if (Array.isArray(body.data)) {
    return {
      rows: body.data as T[],
      total: typeof body.total === 'number' ? body.total : body.data.length,
    }
  }

  if (Array.isArray(body)) {
    return { rows: body as T[], total: body.length }
  }

  return { rows: [], total: typeof body.total === 'number' ? body.total : 0 }
}

/** 提取错误消息 */
export function extractErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return '未知错误'
  const e = error as Record<string, unknown>
  const response = e.response as { data?: Record<string, unknown> } | undefined
  const data = response?.data ?? e
  if (typeof data === 'object' && data !== null) {
    const d = data as Record<string, unknown>
    const msg = d.msg ?? d.message ?? d.detail
    if (typeof msg === 'string') return msg
    if (Array.isArray(d.detail)) {
      return d.detail.map((x) => (typeof x === 'object' && x && 'msg' in x ? String((x as { msg: string }).msg) : String(x))).join('; ')
    }
  }
  if (typeof e.message === 'string') return e.message
  return '未知错误'
}

/** Python snake_case 教材 → manage camelCase */
export function mapPythonTextbook(raw: Record<string, unknown>) {
  return {
    textbookId: Number(raw.textbookId ?? raw.id ?? 0),
    title: String(raw.title ?? raw.name ?? ''),
    subject: raw.subject as string | undefined,
    version: raw.version as string | undefined,
    status: String(raw.status ?? 'draft'),
    description: raw.description as string | undefined,
    createTime: (raw.createTime ?? raw.created_at) as string | undefined,
    tableName: raw.tableName as string | undefined,
    name: raw.name as string | undefined,
    educationLevel: raw.educationLevel as string | undefined,
    category: raw.category as string | undefined,
  }
}

/** Python snake_case 章节 → manage camelCase */
export function mapPythonChapter(raw: Record<string, unknown>) {
  return {
    chapterId: Number(raw.chapterId ?? raw.id ?? 0),
    textbookId: Number(raw.textbookId ?? raw.textbook_id ?? 0),
    chapterNumber: Number(raw.chapterNumber ?? raw.chapter_number ?? 0),
    title: String(raw.title ?? ''),
    contentId: (raw.contentId ?? raw.content_id) as string | undefined,
    status: String(raw.status ?? 'draft'),
    createTime: (raw.createTime ?? raw.created_at) as string | undefined,
  }
}
